import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { talentCommitteesApi } from '@/api/talentCommitteesApi';
import { appraisalCycleKeys } from '@/queries/useAppraisalCycles';
import { calibrationMatrixKeys } from '@/queries/useCalibrationMatrix';
import { tcRunKeys } from '@/queries/useTcRun';
import type {
  CreateTcRequest,
  UpdateTcRequest,
  AddTcAppraiseeRequest,
  AddTcMembersRequest,
} from '@/api/types/talentCommittee.types';

export const talentCommitteeKeys = {
  all: ['talentCommittees'] as const,
  list: ['talentCommittees', 'list'] as const,
  appraiseeCandidates: ['talentCommittees', 'appraiseeCandidates'] as const,
  memberCandidates: ['talentCommittees', 'memberCandidates'] as const,
};

export function useTalentCommittees() {
  return useQuery({
    queryKey: talentCommitteeKeys.list,
    queryFn: () => talentCommitteesApi.getTalentCommittees(),
  });
}

export function useAppraiseeCandidates(search?: string) {
  return useQuery({
    queryKey: [...talentCommitteeKeys.appraiseeCandidates, search],
    queryFn: () => talentCommitteesApi.getAppraiseeCandidates(search),
  });
}

export function useMemberCandidates(search?: string) {
  return useQuery({
    queryKey: [...talentCommitteeKeys.memberCandidates, search],
    queryFn: () => talentCommitteesApi.getMemberCandidates(search),
  });
}

export function useCreateTc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateTcRequest) => talentCommitteesApi.createTalentCommittee(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentCommitteeKeys.list });
    },
  });
}

export function useUpdateTc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request, rowVersion }: { id: number; request: UpdateTcRequest; rowVersion?: string }) =>
      talentCommitteesApi.updateTalentCommittee(id, request, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentCommitteeKeys.list });
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: talentCommitteeKeys.list });
      }
    },
  });
}

export function useDeleteTc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => talentCommitteesApi.deleteTalentCommittee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentCommitteeKeys.list });
    },
  });
}

export function useAddAppraisee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tcId, request }: { tcId: number; request: AddTcAppraiseeRequest }) =>
      talentCommitteesApi.addAppraisee(tcId, request),
    onSuccess: (_, { tcId }) => {
      queryClient.invalidateQueries({ queryKey: talentCommitteeKeys.list });
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      queryClient.invalidateQueries({ queryKey: tcRunKeys.detail(tcId) });
      queryClient.invalidateQueries({ queryKey: calibrationMatrixKeys.all });
    },
  });
}

export function useRemoveAppraisee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tcId, appraisalId }: { tcId: number; appraisalId: number }) =>
      talentCommitteesApi.removeAppraisee(tcId, appraisalId),
    onSuccess: (_, { tcId }) => {
      queryClient.invalidateQueries({ queryKey: talentCommitteeKeys.list });
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      queryClient.invalidateQueries({ queryKey: tcRunKeys.detail(tcId) });
      queryClient.invalidateQueries({ queryKey: calibrationMatrixKeys.all });
    },
  });
}

export function useAddMembers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tcId, request }: { tcId: number; request: AddTcMembersRequest }) =>
      talentCommitteesApi.addMembers(tcId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentCommitteeKeys.list });
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tcId, staffId }: { tcId: number; staffId: number }) =>
      talentCommitteesApi.removeMember(tcId, staffId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentCommitteeKeys.list });
    },
  });
}

export function useImportTcs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => talentCommitteesApi.importTalentCommittees(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentCommitteeKeys.list });
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      queryClient.invalidateQueries({ queryKey: calibrationMatrixKeys.all });
    },
  });
}

export function useExportTemplate() {
  return useMutation({
    mutationFn: () => talentCommitteesApi.getTemplate(),
  });
}
